import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import { DEFAULT_FOOTER, IFooter } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { AppConfigService } from '../../app-config.service';
import { FooterGateway } from './footer.gateway';

@Injectable({
  providedIn: 'root',
})
export class FooterStore {
  private readonly gateway: FooterGateway = new FooterGateway();
  private readonly configService: AppConfigService = inject(AppConfigService);

  readonly #footerResource: ResourceRef<IApiResponse<IFooter> | undefined> =
    resource({
      loader: (): Promise<IApiResponse<IFooter>> => {
        return firstValueFrom(this.gateway.get());
      },
    });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#footerResource.isLoading()
  );
  readonly hasLoaded: Signal<boolean> = computed((): boolean =>
    this.#footerResource.hasValue()
  );
  readonly footer: Signal<IFooter> = computed((): IFooter => {
    return this.#footerResource.hasValue()
      ? (this.#footerResource.value()?.data ?? DEFAULT_FOOTER)
      : DEFAULT_FOOTER;
  });

  reload(): void {
    this.#footerResource.reload();
  }

  updateFooter(query: IFooter): void {
    this.#footerResource.update((): IApiResponse<IFooter> => {
      return {
        data: query,
      };
    });
  }

  async save(query: IFooter): Promise<IFooter | undefined> {
    const result: IApiResponse<IFooter> = await firstValueFrom(
      this.gateway.save(query)
    );
    if (result?.data) {
      this.updateFooter(result.data);
    }
    return result?.data;
  }

  requestPath(imageId: string | undefined, area: string): string {
    return (
      this.configService.get().general.cmsUrl +
      '/footer/' +
      area +
      '/image/' +
      imageId
    );
  }

  backgroundImagePath(imageId: string | undefined): string {
    return this.configService.get().general.cmsUrl + '/images/' + imageId;
  }
}
